import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import { type Palette } from "@mui/material/styles";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sorted } from "src/helpers";
import {
  ReviewerTrackingDailyEntry,
  useGetReviewerTrackingDailyQuery,
} from "src/store/api/generatedApi";

const buildTraceColors = (palette: Palette["data"]): string[] => [
  palette.blue,
  palette.pink,
  palette.lightblue,
  palette.orange,
  palette.green,
  palette.red,
];

type TenantDescriptor = Readonly<{
  tenantName: string;
  tenantLongName: string;
}>;

type CombinedReviewerActionsChartProps = Readonly<{
  tenants: TenantDescriptor[];
  start: string;
  end: string;
}>;

type TenantLoadState = Readonly<{
  entries: ReviewerTrackingDailyEntry[];
  isLoading: boolean;
}>;

type OnTenantLoaded = (
  tenantName: string,
  entries: ReviewerTrackingDailyEntry[],
  isLoading: boolean
) => void;

const TenantDataLoader = ({
  tenantName,
  start,
  end,
  onLoaded,
}: {
  tenantName: string;
  start: string;
  end: string;
  onLoaded: OnTenantLoaded;
}) => {
  const { data, isLoading } = useGetReviewerTrackingDailyQuery(
    { tenantName, start, end },
    { skip: !start || !end }
  );
  const entries = data?.entries;

  useEffect(() => {
    onLoaded(tenantName, entries ?? [], isLoading);
  }, [tenantName, entries, isLoading, onLoaded]);

  return null;
};

const totalChanges = (entry: ReviewerTrackingDailyEntry): number =>
  entry.events_created + entry.events_deleted + entry.events_modified;

export const CombinedReviewerActionsChart = ({
  tenants,
  start,
  end,
}: CombinedReviewerActionsChartProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { data: palette, text } = theme.palette;
  const traceColors = useMemo(() => buildTraceColors(palette), [palette]);

  const [stateByTenant, setStateByTenant] = useState<
    Record<string, TenantLoadState>
  >({});

  const handleTenantLoaded: OnTenantLoaded = useCallback(
    (tenantName, entries, isLoading) => {
      setStateByTenant((previous) => {
        const existing = previous[tenantName];
        if (existing?.entries === entries && existing.isLoading === isLoading) {
          return previous;
        }
        return { ...previous, [tenantName]: { entries, isLoading } };
      });
    },
    []
  );

  const allResolved = tenants.every((tenant) => {
    const state = stateByTenant[tenant.tenantName];
    return state !== undefined && !state.isLoading;
  });

  const allEmpty =
    allResolved &&
    tenants.every(
      (tenant) => (stateByTenant[tenant.tenantName]?.entries ?? []).length === 0
    );

  const traces: Partial<PlotData>[] = useMemo(() => {
    const locationTraces = tenants.flatMap((tenant) => {
      const entries = stateByTenant[tenant.tenantName]?.entries ?? [];
      const locationNames = sorted(
        [...new Set(entries.map((entry) => entry.location_name))],
        (name) => name
      );
      return locationNames.map((name) => ({
        label: `${tenant.tenantLongName} – ${name}`,
        entries: entries.filter((entry) => entry.location_name === name),
      }));
    });

    return locationTraces.map((trace, index) => {
      const byDate = sorted([...trace.entries], (entry) => entry.footage_date);
      const color = traceColors[index % traceColors.length];
      return {
        x: byDate.map((entry) => entry.footage_date),
        y: byDate.map(totalChanges),
        type: "scatter" as const,
        mode: "lines+markers" as const,
        name: trace.label,
        marker: { color, size: 5 },
        line: { color, width: 3 },
      };
    });
  }, [tenants, stateByTenant, traceColors]);

  const maxValue = useMemo(() => {
    const allValues = tenants.flatMap((tenant) =>
      (stateByTenant[tenant.tenantName]?.entries ?? []).map(totalChanges)
    );
    return allValues.length > 0 ? Math.max(...allValues, 1) : 1;
  }, [tenants, stateByTenant]);

  const yPadding = maxValue * 1.1;

  return (
    <>
      {tenants.map((tenant) => (
        <TenantDataLoader
          key={tenant.tenantName}
          tenantName={tenant.tenantName}
          start={start}
          end={end}
          onLoaded={handleTenantLoaded}
        />
      ))}
      {!allResolved ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : allEmpty ? (
        <Alert severity="info">{t("noDataAvailable")}</Alert>
      ) : (
        <Plot
          data={traces}
          layout={{
            autosize: true,
            xaxis: { type: "date" },
            yaxis: {
              title: { text: t("totalActions") },
              range: [0, yPadding],
              zeroline: true,
              zerolinewidth: 2,
              zerolinecolor: text.primary,
            },
            margin: { l: 60, r: 30, t: 30, b: 60 },
            hovermode: "x unified",
            plot_bgcolor: "transparent",
            paper_bgcolor: "transparent",
            legend: { orientation: "h", y: -0.2 },
          }}
          config={{
            responsive: true,
            displayModeBar: false,
            displaylogo: false,
          }}
          style={{ width: "100%", height: "350px" }}
          useResizeHandler
        />
      )}
    </>
  );
};
